/**
 * Persisted in-app notification feed (PROD-383) — wire types + TanStack
 * Query hooks over `/api/notifications/*`.
 *
 * NOT the same module as `api/notifications.ts`, which is the
 * notification-PREFERENCES catalog (category labels + default channel
 * matrix for the Settings card). This module is the feed itself: the
 * `Notification` rows the payment/billing producers write.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiFetch } from './http'

export interface NotificationFeedItem {
  id: string
  category: string
  title: string
  body: string | null
  deepLinkUri: string | null
  subjectKind: string | null
  subjectId: string | null
  payload: unknown
  actor: { id: string; name: string; email: string | null; image: string | null } | null
  /** ISO timestamps on the wire. */
  readAt: string | null
  clearedAt: string | null
  createdAt: string
}

export interface NotificationFeedResponse {
  notifications: NotificationFeedItem[]
  unreadCount: number
  generatedAt: string
}

export const NOTIFICATIONS_QUERY_KEY = ['notifications', 'me'] as const

export async function fetchMyNotifications(
  vars: { limit?: number; onlyUnread?: boolean } = {},
  signal?: AbortSignal,
): Promise<NotificationFeedResponse> {
  return apiFetch<NotificationFeedResponse>('/api/notifications', {
    query: {
      limit: vars.limit ?? undefined,
      unread: vars.onlyUnread ? 'true' : undefined,
    },
    signal,
  })
}

export async function markNotificationRead(id: string): Promise<void> {
  await apiFetch<{ ok: boolean }>(`/api/notifications/${encodeURIComponent(id)}/read`, { method: 'POST' })
}

export async function markAllNotificationsRead(): Promise<number> {
  const { count } = await apiFetch<{ ok: boolean; count: number }>('/api/notifications/mark-all-read', {
    method: 'POST',
  })
  return count
}

export async function clearNotification(id: string): Promise<void> {
  await apiFetch<{ ok: boolean }>(`/api/notifications/${encodeURIComponent(id)}/clear`, { method: 'POST' })
}

/**
 * The feed, polled on the tasks-bell cadence (30s — catches cross-tab
 * reads); TanStack's default refetch-on-focus covers tab switches.
 */
export function useMyNotificationsQuery() {
  return useQuery<NotificationFeedResponse>({
    queryKey: NOTIFICATIONS_QUERY_KEY,
    queryFn: ({ signal }) => fetchMyNotifications({}, signal),
    refetchInterval: 30_000,
    staleTime: 30_000,
  })
}

export function useMarkNotificationReadMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => markNotificationRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY }),
  })
}

export function useMarkAllNotificationsReadMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => markAllNotificationsRead(),
    onSuccess: () => qc.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY }),
  })
}

export function useClearNotificationMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => clearNotification(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY }),
  })
}
